/** @format — Documents page (grid + list UI) */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageTitle from "@/components/layout/PageTitle";
import { useIntersectionLoadMore } from "@/hooks/useIntersectionLoadMore";
import DocumentsToolbar from "./components/DocumentsToolbar";
import DocumentsGrid from "./components/DocumentsGrid";
import DocumentsList from "./components/DocumentsList";
import DocumentsEmptyState from "./components/DocumentsEmptyState";
import DocumentsSearchEmptyState from "./components/DocumentsSearchEmptyState";
import DocumentsFiltersEmptyState from "./components/DocumentsFiltersEmptyState";
import DocumentsMobileHeader from "./components/DocumentsMobileHeader";
import DocumentsGridSkeleton from "./components/DocumentsGridSkeleton";
import DocumentsListSkeleton from "./components/DocumentsListSkeleton";
import DocumentsShipmentGrid from "./components/DocumentsShipmentGrid";
import { useDocumentsList } from "./hooks/useDocumentsList";
import { useDocumentsListings } from "./hooks/useDocumentsListings";
import { DOCUMENT_TYPE_OPTIONS } from "./utils/documentFormatters";
import type {
  CustomDateRange,
  DocumentDateFilter,
  DocumentItem,
  DocumentSortBy,
  DocumentSortOrder,
  DocumentsViewMode,
} from "./types";

function parseViewMode(value: string | null): DocumentsViewMode {
  return value === "list" ? "list" : "grid";
}

function DocumentsCountLabel({
  count,
  searchQuery,
  label,
  isUpdating,
}: {
  count: number;
  searchQuery: string;
  label?: string;
  isUpdating?: boolean;
}) {
  const trimmed = searchQuery.trim();

  if (label) {
    return (
      <p className="shrink-0 text-xs font-semibold uppercase text-[#344054]">
        {label}
        {isUpdating ? " · updating" : ""}
      </p>
    );
  }

  if (trimmed) {
    return (
      <p className="shrink-0 text-xs font-semibold uppercase text-[#344054]">
        {count} search results for &ldquo;{trimmed.toUpperCase()}&rdquo;
        {isUpdating ? " · updating" : ""}
      </p>
    );
  }

  return (
    <p className="shrink-0 text-xs font-semibold uppercase text-[#344054]">
      {count} items{isUpdating ? " · updating" : ""}
    </p>
  );
}

function DocumentsPageShell({ onMenuClick, children }: { onMenuClick?: () => void; children: React.ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
      <DocumentsMobileHeader onMenuClick={onMenuClick} />
      <div className="hidden md:block">
        <PageTitle>Documents</PageTitle>
      </div>
      {children}
    </div>
  );
}

export default function DocumentsPage({ onMenuClick }: { onMenuClick?: () => void }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [type, setType] = useState("all");
  const [dateFilter, setDateFilter] = useState<DocumentDateFilter | string>("all");
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({});
  const [sortBy, setSortBy] = useState<DocumentSortBy>(() =>
    searchParams.get("sort") === "listing" ? "listing" : "date_created",
  );
  const [sortOrder, setSortOrder] = useState<DocumentSortOrder>("newest");
  const viewMode = parseViewMode(searchParams.get("view"));
  const showShipmentsView = sortBy === "listing";

  useEffect(() => {
    setSearchParams(
      (params) => {
        const updated = new URLSearchParams(params);
        if (sortBy === "listing") updated.set("sort", "listing");
        else updated.delete("sort");
        return updated;
      },
      { replace: true },
    );
  }, [sortBy, setSearchParams]);

  const handleSortByChange = (value: DocumentSortBy) => {
    setSortBy(value);
  };

  const handleViewModeChange = (mode: DocumentsViewMode) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (mode === "grid") next.delete("view");
        else next.set("view", mode);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const documentsQuery = useDocumentsList({
    search: debouncedSearch,
    type,
    dateFilter,
    customDateRange,
    sortBy,
    sortOrder,
    enabled: !showShipmentsView,
  });

  const listingsQuery = useDocumentsListings({
    search: debouncedSearch,
  });

  const {
    documents,
    total,
    filteredCount,
    isPending: documentsPending,
    isFetching,
    isFetchingMore,
    hasMore,
    loadMore,
  } = documentsQuery;

  const {
    listings,
    total: listingsTotal,
    isPending: listingsPending,
    isFetchingMore: listingsFetchingMore,
    hasMore: listingsHasMore,
    loadMore: loadMoreListings,
  } = listingsQuery;

  const loadMoreSentinelRef = useIntersectionLoadMore({
    enabled: showShipmentsView ? listingsHasMore : hasMore,
    isLoading: showShipmentsView ? listingsFetchingMore : isFetchingMore,
    onLoadMore: () => (showShipmentsView ? loadMoreListings() : loadMore()),
    rootRef: scrollRef,
    observeKey: showShipmentsView ? listings.length : documents.length,
    resetKey: `${debouncedSearch}|${type}|${dateFilter}|${sortBy}|${sortOrder}|${viewMode}`,
  });

  const typeOptions = useMemo(() => {
    const known = new Set(DOCUMENT_TYPE_OPTIONS.map((option) => option.value));
    const extras = Array.from(
      new Set(
        documents
          .map((doc) => String(doc.uploadType || "").toLowerCase())
          .filter((value) => value && !known.has(value)),
      ),
    ).map((value) => ({
      value,
      label: value.replace(/_/g, " ").toUpperCase(),
    }));
    return [...DOCUMENT_TYPE_OPTIONS, ...extras];
  }, [documents]);

  const handleOpen = (document: DocumentItem) => {
    const target = document.fileUrl || document.previewUrl;
    if (target) {
      window.open(target, "_blank", "noopener,noreferrer");
      return;
    }
    toast.error("No file available to open");
  };

  const handleGoToShipment = (document: DocumentItem, event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!document.shipmentId) {
      toast.error("No shipment linked to this document");
      return;
    }
    navigate(`/shipments/${document.shipmentId}`);
  };

  const handleToggleDateSort = () => {
    setSortOrder((current) => (current === "newest" ? "oldest" : "newest"));
  };

  const hasActiveSearch = debouncedSearch.trim().length > 0;
  const hasActiveFilters = type !== "all" || dateFilter !== "all";
  const isInitialLoading = showShipmentsView ? listingsPending : documentsPending;
  const showGlobalEmpty =
    !isInitialLoading && !showShipmentsView && total === 0 && !hasActiveSearch && !hasActiveFilters;
  const showNoSearchResults =
    !isInitialLoading &&
    !showShipmentsView &&
    filteredCount === 0 &&
    (hasActiveSearch || hasActiveFilters);
  const showFiltersEmpty =
    !isInitialLoading && !showShipmentsView && filteredCount === 0 && hasActiveFilters && !hasActiveSearch;
  const displayCount = showShipmentsView ? listingsTotal : hasActiveFilters ? filteredCount : total;

  const contentClass =
    "flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-4 pb-6 pt-3 md:gap-4 md:px-5";

  const toolbar = (
    <DocumentsToolbar
      search={searchInput}
      onSearchChange={setSearchInput}
      type={type}
      onTypeChange={setType}
      dateFilter={dateFilter}
      customDateRange={customDateRange}
      onDateFilterChange={setDateFilter}
      onCustomDateRangeChange={setCustomDateRange}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortByChange={handleSortByChange}
      onSortOrderChange={setSortOrder}
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      typeOptions={typeOptions}
    />
  );

  if (isInitialLoading) {
    return (
      <DocumentsPageShell onMenuClick={onMenuClick}>
        <div className={contentClass}>
          {toolbar}
          <div className="min-h-0 w-full flex-1 overflow-y-auto">
            {showShipmentsView ? (
              <DocumentsGridSkeleton count={6} columns={3} />
            ) : viewMode === "grid" ? (
              <DocumentsGridSkeleton />
            ) : (
              <DocumentsListSkeleton />
            )}
          </div>
        </div>
      </DocumentsPageShell>
    );
  }

  if (showGlobalEmpty) {
    return (
      <DocumentsPageShell onMenuClick={onMenuClick}>
        <DocumentsEmptyState />
      </DocumentsPageShell>
    );
  }

  if (showFiltersEmpty) {
    return (
      <DocumentsPageShell onMenuClick={onMenuClick}>
        <div className={contentClass}>
          {toolbar}
          <DocumentsFiltersEmptyState />
        </div>
      </DocumentsPageShell>
    );
  }

  if (showNoSearchResults) {
    return (
      <DocumentsPageShell onMenuClick={onMenuClick}>
        <div className={contentClass}>
          {toolbar}
          <DocumentsSearchEmptyState query={debouncedSearch} />
        </div>
      </DocumentsPageShell>
    );
  }

  return (
    <DocumentsPageShell onMenuClick={onMenuClick}>
      <div className={contentClass}>
        {toolbar}

        <DocumentsCountLabel
          count={displayCount}
          searchQuery={debouncedSearch}
          label={showShipmentsView ? `${displayCount} shipments` : undefined}
          isUpdating={isFetching && !isFetchingMore}
        />

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-4">
          {showShipmentsView ? (
            <>
              <DocumentsShipmentGrid
                listings={listings}
                onSelect={(listing) => navigate(`/documents/shipment/${listing.listingId}`)}
              />
              {listingsFetchingMore ? (
                <div className="mt-4">
                  <DocumentsGridSkeleton count={3} columns={3} />
                </div>
              ) : null}
            </>
          ) : filteredCount > 0 ? (
            <>
              {viewMode === "grid" ? (
                <DocumentsGrid
                  documents={documents}
                  onOpen={handleOpen}
                  onGoToShipment={(doc) => handleGoToShipment(doc)}
                />
              ) : (
                <DocumentsList
                  documents={documents}
                  onOpen={handleOpen}
                  onGoToShipment={(doc, event) => handleGoToShipment(doc, event)}
                  onToggleDateSort={handleToggleDateSort}
                />
              )}

              {isFetchingMore && viewMode === "grid" ? (
                <div className="mt-4">
                  <DocumentsGridSkeleton count={5} />
                </div>
              ) : null}

              {isFetchingMore && viewMode === "list" ? (
                <p className="py-4 text-center text-xs text-neutral-500">Loading more documents…</p>
              ) : null}
            </>
          ) : (
            <DocumentsSearchEmptyState query={debouncedSearch} />
          )}

          <div ref={loadMoreSentinelRef} className="h-1 w-full shrink-0" aria-hidden />
        </div>
      </div>
    </DocumentsPageShell>
  );
}
